import { onScopeDispose, toValue, watch, type MaybeRefOrGetter } from "vue";
import { useDraftSettings } from "@/composables/useDraftSettings";
import { deleteDraft, saveDraft, type DraftData, type DraftKind } from "@/helper/drafts";

const SAVE_DELAY_MS = 300;

export interface UseDraftOptions {
  kind: DraftKind;
  /** What the draft belongs to; a getter, because dialogs stay mounted while their target changes. */
  scope: MaybeRefOrGetter<string>;
  label: MaybeRefOrGetter<string>;
  /** The state to keep. Must not contain secrets such as password values. */
  snapshot: () => DraftData;
  /** Nothing is written while this is `false`, e.g. while the dialog is closed. */
  enabled?: MaybeRefOrGetter<boolean>;
}

/**
 * Keeps the state of a dialog in localStorage while it changes, so a crash or reload does not lose it.
 * Call `markPristine()` once the dialog is open and seeded with its start state (a restored draft is applied after that),
 * `clear()` after a successful save and `cancel()` when the dialog is closed without saving.
 */
export function useDraft(options: UseDraftOptions) {
  const { keepDraftOnCancel } = useDraftSettings();
  let baseline = JSON.stringify(options.snapshot());
  let suspended = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function stopTimer() {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }

  function persist() {
    timer = null;
    if (suspended || !toValue(options.enabled ?? true)) return;
    const data = options.snapshot();
    if (JSON.stringify(data) === baseline) {
      deleteDraft(options.kind, toValue(options.scope));
      return;
    }
    saveDraft(options.kind, toValue(options.scope), toValue(options.label), data);
  }

  function schedule() {
    if (suspended) return;
    stopTimer();
    timer = setTimeout(persist, SAVE_DELAY_MS);
  }

  watch(() => options.snapshot(), schedule, { deep: true });

  /**
   * The state without any edit is the start state: edits are measured against it, and saving drafts is on again.
   * Defaults to the current snapshot, pass the snapshot of the untouched dialog when it already shows a restored draft.
   */
  function markPristine(pristine?: DraftData) {
    stopTimer();
    baseline = JSON.stringify(pristine ?? options.snapshot());
    suspended = false;
  }

  /** The dialog saved its data: the draft is obsolete, and stays off until `markPristine()` runs for the next open. */
  function clear() {
    stopTimer();
    suspended = true;
    deleteDraft(options.kind, toValue(options.scope));
  }

  /** The dialog was closed without saving: keeps the draft, or deletes it when the user turned that setting off. */
  function cancel() {
    if (keepDraftOnCancel.value) {
      persist();
      stopTimer();
      return;
    }
    clear();
  }

  onScopeDispose(stopTimer);

  return { markPristine, clear, cancel, flush: persist };
}
